import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Loader2,
  ShieldCheck,
  Scale,
  FileText,
  Languages,
  Check,
  ChevronDown,
  Lock,
  AlertTriangle,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api } from "@/lib/api";

// Documents that require explicit acceptance, in display order.
const REQUIRED_DOCS = [
  { slug: "privacy-notice", label: "Privacy Notice", icon: ShieldCheck },
  { slug: "terms-of-service", label: "Terms of Service", icon: Scale },
  { slug: "privacy-policy", label: "Privacy Policy", icon: FileText },
];

const LANG_LABELS = {
  en: "English",
  hi: "हिन्दी",
  ml: "മലയാളം",
  ta: "தமிழ்",
  kn: "ಕನ್ನಡ",
};

export default function LegalConsent() {
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionId = searchParams.get("session");
  const isPreview = !sessionId;

  const [docs, setDocs] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [successPhase, setSuccessPhase] = useState("idle"); // idle | success | returning
  const [openSlug, setOpenSlug] = useState(REQUIRED_DOCS[0].slug);

  // sessionStatus: null (no session/preview) | "pending" | "expired" | "completed" | "invalid"
  const [sessionStatus, setSessionStatus] = useState(isPreview ? null : "loading");
  const [userLabel, setUserLabel] = useState(null);

  const initialLang = (searchParams.get("lang") || "en").toLowerCase();
  const [lang, setLang] = useState(LANG_LABELS[initialLang] ? initialLang : "en");

  useEffect(() => {
    document.title = "Review & Accept · Careable 360+ Plus";
  }, []);

  // Languages list (non-fatal)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/legal/languages");
        if (!cancelled) setLanguages(data || []);
      } catch {
        if (!cancelled)
          setLanguages([{ code: "en", label: "English", native: "English" }]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Session context (status + lang lock)
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/consent/session/${sessionId}/context`);
        if (cancelled) return;
        setSessionStatus(data.status || "pending");
        setUserLabel(data.user_label || null);
        if (data.lang && LANG_LABELS[data.lang]) setLang(data.lang);
      } catch {
        if (!cancelled) setSessionStatus("invalid");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  // Document content for the active language
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get("/legal", { params: { lang } });
        if (!cancelled) {
          setDocs(data || []);
          setError("");
        }
      } catch (err) {
        if (!cancelled)
          setError(err?.response?.data?.detail || err.message || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [lang]);

  const docMap = useMemo(() => {
    const map = {};
    for (const d of docs) map[d.slug] = d;
    return map;
  }, [docs]);

  const latestUpdated = useMemo(() => {
    const dates = REQUIRED_DOCS.map((d) => docMap[d.slug]?.updated_at).filter(Boolean);
    if (!dates.length) return null;
    return dates.sort().slice(-1)[0];
  }, [docMap]);

  const updateLang = (code) => {
    setLang(code);
    const next = new URLSearchParams(searchParams);
    if (code === "en") next.delete("lang");
    else next.set("lang", code);
    setSearchParams(next, { replace: true });
  };

  const handleAccept = async () => {
    if (isPreview) {
      setSubmitted(true);
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.post("/consent", {
        session_id: sessionId,
        accepted: true,
      });
      setSubmitted(true);
      setSuccessPhase("success");

      // Notify the opener (partner React app) that consent is complete.
      // Safe to use "*" — payload contains no secrets, only public status info.
      const completionMessage = {
        type: "careable.consent.completed",
        session_id: sessionId,
        status: "completed",
      };
      try {
        if (window.opener && !window.opener.closed) {
          window.opener.postMessage(completionMessage, "*");
        }
        if (window.parent && window.parent !== window) {
          window.parent.postMessage(completionMessage, "*");
        }
      } catch (_) {
        /* postMessage best-effort */
      }

      // Attempt to auto-close the window (works for popups opened via window.open).
      // If close is blocked or there's no opener, fall back to redirect_url or success screen.
      const hasOpener =
        typeof window !== "undefined" && window.opener && !window.opener.closed;

      if (hasOpener) {
        setRedirecting(true);
        // Tiny success animation then phase 2 then close
        setTimeout(() => setSuccessPhase("returning"), 750);
        setTimeout(() => {
          try {
            window.close();
          } catch (_) {
            /* ignore */
          }
        }, 1600);
      } else if (data.redirect_url) {
        setRedirecting(true);
        setTimeout(() => setSuccessPhase("returning"), 750);
        setTimeout(() => {
          window.location.href = data.redirect_url;
        }, 1700);
      } else {
        // Best-effort close even without opener (some browsers permit it).
        setTimeout(() => setSuccessPhase("returning"), 800);
        setTimeout(() => {
          try {
            window.close();
          } catch (_) {
            /* ignore */
          }
        }, 1500);
      }
    } catch (err) {
      setError(
        err?.response?.data?.detail || err.message || "Could not record consent."
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Blocking states for invalid / expired / already-used sessions ----
  const blocking =
    !isPreview &&
    ["expired", "completed", "invalid"].includes(sessionStatus) &&
    !submitted;

  if (blocking) {
    const map = {
      expired: {
        title: "This link has expired",
        body: "For your security, the consent link is valid for a limited time. Please return to the app and start again.",
      },
      completed: {
        title: "Already completed",
        body: "This consent has already been recorded. You can safely return to the app.",
      },
      invalid: {
        title: "Invalid link",
        body: "We couldn't find this consent session. Please return to the app and try again.",
      },
    };
    const info = map[sessionStatus] || map.invalid;
    return (
      <div
        data-testid="legal-consent-blocked"
        className="min-h-screen bg-[#F4FAF8] text-[#0E2A36] flex items-center justify-center px-6"
      >
        <div className="max-w-sm text-center">
          <div className="w-14 h-14 rounded-2xl bg-[#FBEFD9] flex items-center justify-center mx-auto mb-5">
            <AlertTriangle className="w-7 h-7 text-[#B27818]" />
          </div>
          <h1 className="font-display text-xl font-bold">{info.title}</h1>
          <p className="text-[14px] text-[#48606B] mt-2 leading-relaxed">{info.body}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="legal-consent-page"
      className="min-h-screen bg-[#F4FAF8] text-[#0E2A36] flex flex-col"
    >
      {isPreview && (
        <div
          data-testid="consent-preview-badge"
          className="bg-[#E8A33D] text-[#1F4E5F] text-center text-[11px] font-bold tracking-[0.18em] uppercase py-1.5"
        >
          Preview · open from the app to record consent
        </div>
      )}

      {/* Compact app-style header */}
      <header className="bg-[#1F4E5F] text-[#F4FAF8] shrink-0">
        <div className="max-w-2xl mx-auto px-5 py-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-white/15 flex items-center justify-center">
                <ShieldCheck className="w-4 h-4 text-[#E8A33D]" />
              </div>
              <div className="leading-tight">
                <p className="font-display font-bold text-[15px]">Careable 360+</p>
                <p className="text-[11px] text-[#F4FAF8]/70">
                  {userLabel ? `For ${userLabel}` : "Review & accept to continue"}
                </p>
              </div>
            </div>
            {latestUpdated && (
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-[0.15em] text-[#F4FAF8]/55">
                  Version
                </p>
                <p className="text-[12px] font-semibold" data-testid="consent-version">
                  {new Date(latestUpdated).toLocaleDateString(undefined, {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })}
                </p>
              </div>
            )}
          </div>

          {languages.length > 1 && (
            <div
              data-testid="consent-lang-switcher"
              className="mt-3 flex items-center gap-1.5 overflow-x-auto"
            >
              <Languages className="w-3.5 h-3.5 text-[#F4FAF8]/60 shrink-0" />
              {languages.map((l) => {
                const isActive = l.code === lang;
                return (
                  <button
                    key={l.code}
                    data-testid={`consent-lang-${l.code}`}
                    onClick={() => updateLang(l.code)}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors border shrink-0 ${
                      isActive
                        ? "bg-[#E8A33D] text-[#1F4E5F] border-[#E8A33D]"
                        : "bg-white/10 text-[#F4FAF8] border-white/20 hover:bg-white/15"
                    }`}
                  >
                    {LANG_LABELS[l.code] || l.native}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </header>

      {/* Scrollable document body */}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto px-5 py-5 pb-44">
          <p className="text-[13px] text-[#48606B] leading-relaxed mb-4">
            To create your account, please read and accept the documents below.
            Your acceptance is recorded with the date, document version and app
            version.
          </p>

          {loading ? (
            <div className="py-24 flex items-center justify-center text-[#6A8290] text-sm">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading documents…
            </div>
          ) : error && !submitted ? (
            <div
              data-testid="consent-error"
              className="py-4 px-4 text-center text-[#C84444] text-sm bg-[#FBE9E9] border border-[#F0C9C9] rounded-2xl"
            >
              {error}
            </div>
          ) : (
            <div className="space-y-3">
              {REQUIRED_DOCS.map((d, i) => {
                const doc = docMap[d.slug];
                const isOpen = openSlug === d.slug;
                const Icon = d.icon;
                return (
                  <div
                    key={d.slug}
                    data-testid={`consent-doc-${d.slug}`}
                    className="bg-white border border-[#C9DDD6] rounded-2xl overflow-hidden shadow-[0_2px_14px_rgba(15,56,32,0.04)]"
                  >
                    <button
                      data-testid={`consent-doc-toggle-${d.slug}`}
                      onClick={() => setOpenSlug(isOpen ? "" : d.slug)}
                      className="w-full flex items-center gap-3 px-4 py-3.5 text-left"
                    >
                      <div className="w-9 h-9 rounded-xl bg-[#E1EEEA] flex items-center justify-center shrink-0">
                        <Icon className="w-4 h-4 text-[#1F4E5F]" />
                      </div>
                      <div className="flex-1 leading-tight">
                        <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#8AA0A8]">
                          Document {i + 1} of {REQUIRED_DOCS.length}
                        </p>
                        <p className="font-semibold text-[15px] text-[#0E2A36] mt-0.5">
                          {doc?.title || d.label}
                        </p>
                      </div>
                      <ChevronDown
                        className={`w-4 h-4 text-[#6A8290] transition-transform ${
                          isOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>

                    {isOpen && (
                      <div className="border-t border-[#EAF1EE]">
                        <div
                          data-testid={`consent-doc-body-${d.slug}`}
                          className="max-h-[42vh] overflow-y-auto px-4 py-4"
                        >
                          {doc ? (
                            <div className="prose prose-careable prose-sm max-w-none">
                              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {doc.content}
                              </ReactMarkdown>
                            </div>
                          ) : (
                            <p className="text-sm text-[#6A8290] py-6 text-center">
                              Document not available in this language.
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* Sticky consent action bar */}
      <div
        data-testid="consent-action-bar"
        className="fixed bottom-0 inset-x-0 bg-white border-t border-[#C9DDD6] shadow-[0_-4px_24px_rgba(15,56,32,0.08)]"
      >
        <div className="max-w-2xl mx-auto px-5 py-4">
          <label
            data-testid="consent-checkbox-label"
            className="flex items-start gap-3 cursor-pointer select-none mb-3"
          >
            <button
              type="button"
              data-testid="consent-checkbox"
              onClick={() => setAccepted((v) => !v)}
              disabled={submitted}
              className={`mt-0.5 w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors ${
                accepted ? "bg-[#1F4E5F] border-[#1F4E5F]" : "bg-white border-[#B7CCC4]"
              }`}
            >
              {accepted && <Check className="w-3.5 h-3.5 text-white" />}
            </button>
            <span className="text-[13px] leading-snug text-[#36505A]">
              I have read and agree to the{" "}
              <span className="font-semibold text-[#1F4E5F]">Privacy Notice</span>,{" "}
              <span className="font-semibold text-[#1F4E5F]">Terms of Service</span> and{" "}
              <span className="font-semibold text-[#1F4E5F]">Privacy Policy</span>.
            </span>
          </label>

          <button
            data-testid="consent-accept-btn"
            disabled={!accepted || submitting || submitted}
            onClick={handleAccept}
            className={`w-full rounded-2xl py-3.5 font-semibold text-[15px] flex items-center justify-center gap-2 transition-all ${
              accepted && !submitting && !submitted
                ? "bg-[#1F4E5F] text-white hover:bg-[#163B49] active:scale-[0.99]"
                : "bg-[#D6E4DF] text-[#8AA0A8] cursor-not-allowed"
            }`}
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Recording…
              </>
            ) : submitted ? (
              <>
                <Check className="w-4 h-4" /> Consent recorded
              </>
            ) : (
              <>
                <Lock className="w-4 h-4" /> Accept &amp; Continue
              </>
            )}
          </button>

          {submitted && (
            <p
              data-testid="consent-result"
              className="mt-3 text-[12px] text-[#48606B] bg-[#EAF5F1] border border-[#C9DDD6] rounded-xl px-3 py-2 leading-relaxed"
            >
              {isPreview ? (
                <>This is a preview — no record was saved. Open this page from the app (with a session) to record consent.</>
              ) : redirecting ? (
                <>Consent saved. Returning you to the app…</>
              ) : (
                <>Consent saved. You can close this window and continue in the app.</>
              )}
            </p>
          )}
        </div>
      </div>

      <SuccessOverlay phase={successPhase} />
    </div>
  );
}

function SuccessOverlay({ phase }) {
  return (
    <AnimatePresence>
      {phase !== "idle" && (
        <motion.div
          key="consent-success-overlay"
          data-testid="consent-success-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.22 }}
          className="fixed inset-0 z-50 bg-[#0E2A36]/85 backdrop-blur-md flex items-center justify-center px-6"
          aria-hidden="true"
        >
          <motion.div
            initial={{ scale: 0.92, opacity: 0, y: 8 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 22 }}
            className="flex flex-col items-center text-center max-w-xs"
          >
            {/* Animated check ring */}
            <div className="relative w-24 h-24 mb-5">
              <motion.span
                initial={{ scale: 0.6, opacity: 0 }}
                animate={{ scale: [0.6, 1.15, 1], opacity: [0, 0.6, 0] }}
                transition={{ duration: 1.1, ease: "easeOut" }}
                className="absolute inset-0 rounded-full bg-[#5BAE71]/40"
              />
              <motion.div
                initial={{ scale: 0.4, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 320, damping: 18, delay: 0.05 }}
                className="absolute inset-0 rounded-full bg-[#5BAE71] flex items-center justify-center shadow-[0_8px_30px_rgba(91,174,113,0.45)]"
              >
                <svg
                  viewBox="0 0 52 52"
                  className="w-14 h-14"
                  fill="none"
                  stroke="white"
                  strokeWidth="5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <motion.path
                    d="M14 27 L23 36 L39 18"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 0.45, delay: 0.25, ease: "easeOut" }}
                  />
                </svg>
              </motion.div>
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={phase}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.25 }}
              >
                <p className="font-display text-[22px] font-bold text-white tracking-tight">
                  {phase === "returning" ? "Returning to app…" : "Consent saved"}
                </p>
                <p className="text-[13px] text-white/70 mt-1.5 leading-relaxed">
                  {phase === "returning"
                    ? "You can finish registration in a moment."
                    : "Your acceptance has been recorded securely."}
                </p>
              </motion.div>
            </AnimatePresence>

            {phase === "returning" && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.15 }}
                className="mt-5 flex items-center gap-2 text-white/80 text-[12px]"
              >
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                <span>Closing this window…</span>
              </motion.div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
